// ============================================================================
// AuthContext — JWT-based auth + Stripe upgrade flow
// Stores: token in localStorage, user in memory
// ============================================================================
import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { authApi, paymentsApi } from '../services/apiService';

const AuthContext = createContext(null);

export const TOKEN_KEY = 'tc-auth-token';

export function AuthProvider({ children }) {
  const [user, setUser]         = useState(null);
  const [token, setToken]       = useState(() => localStorage.getItem(TOKEN_KEY));
  const [loading, setLoading]   = useState(true);

  // Hydrate user from stored token on mount
  useEffect(() => {
    if (!token) { setLoading(false); return; }
    authApi.me(token)
      .then(res => setUser(res.data.user))
      .catch(() => {
        localStorage.removeItem(TOKEN_KEY);
        setToken(null);
      })
      .finally(() => setLoading(false));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const login = useCallback(async (email, password) => {
    const res = await authApi.login(email, password);
    const { token: t, user: u } = res.data;
    localStorage.setItem(TOKEN_KEY, t);
    setToken(t);
    setUser(u);
    return u;
  }, []);

  const register = useCallback(async (email, password) => {
    const res = await authApi.register(email, password);
    const { token: t, user: u } = res.data;
    localStorage.setItem(TOKEN_KEY, t);
    setToken(t);
    setUser(u);
    return u;
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem(TOKEN_KEY);
    setToken(null);
    setUser(null);
  }, []);

  // Re-fetch the current user (e.g. after email verification or Pro grant)
  const refreshUser = useCallback(async () => {
    if (!token) return null;
    const res = await authApi.me(token);
    setUser(res.data.user);
    return res.data.user;
  }, [token]);

  const resendVerification = useCallback(async () => {
    if (!token) throw new Error('Login required');
    return authApi.resendVerification(token);
  }, [token]);

  const startCheckout = useCallback(async () => {
    if (!token) throw new Error('Login required');
    const res = await paymentsApi.checkout(token);
    window.location.href = res.data.url;
  }, [token]);

  const openPortal = useCallback(async () => {
    if (!token) throw new Error('Login required');
    const res = await paymentsApi.portal(token);
    window.location.href = res.data.url;
  }, [token]);

  const isPaid = user?.tier === 'paid';

  return (
    <AuthContext.Provider value={{ user, token, loading, login, register, logout, startCheckout, openPortal, isPaid, refreshUser, resendVerification }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>');
  return ctx;
}
